import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import {
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
} from '@/components/ui-kit/sidebar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui-kit/collapsible';
import { SidebarMenuGroupProps } from '@/types/sidebar.type';
import { MenuIcon, MenuIconName, SIDEBAR_NAV_ICON_SIZE } from '@/components/core';
import { cn } from '@/lib/utils';
import { matchesSidebarPath } from '@/lib/utils/sidebar-utils';
import { navButtonClassName } from '../sidebar-menu-item/sidebar-menu-Item';

const strokeWidth = 2.2;

/**
 * A top-level sidebar group (e.g. "Attendance", "Leave Management") — icon + label + chevron
 * header that only toggles open/closed and never navigates (its `items` have no page of their
 * own), matching the Angular app's `type: 'collapsable'` menuList entries. Open/closed state is
 * owned by AppSidebar so only one group is open at a time and it stays in sync with the active
 * route; this component is purely a controlled view over that state.
 */
export const SidebarMenuGroupComponent = ({
  title,
  icon,
  items,
  showText,
  isRail,
  isOpen,
  onToggle,
  onItemClick,
}: Readonly<SidebarMenuGroupProps>) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const isParentActive = items.some((child) => matchesSidebarPath(pathname, child.path));

  // When a child's path is a prefix of a sibling's (e.g. "/contacts" and
  // "/contacts/custom-fields"), both would match via matchesSidebarPath. Only the
  // child with the longest (most specific) matching path should highlight.
  const activeChildId = items
    .filter((child) => matchesSidebarPath(pathname, child.path))
    .sort((a, b) => b.path.length - a.path.length)[0]?.id;

  const renderIcon = () => (
    <div className="flex size-[18px] shrink-0 items-center justify-center">
      <MenuIcon
        name={icon as MenuIconName}
        size={SIDEBAR_NAV_ICON_SIZE}
        strokeWidth={strokeWidth}
        weight={isParentActive ? 'fill' : 'regular'}
        className={
          isParentActive ? 'text-primary-600' : 'text-neutral-700 group-hover/menu-item:text-neutral-700'
        }
      />
    </div>
  );

  const labelClassName = cn(
    'nav-label truncate text-sm',
    !showText && 'hidden',
    'group-data-[collapsible=icon]:hidden',
    isParentActive ? 'text-primary-600' : 'text-neutral-700'
  );

  if (isRail) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          asChild
          isActive={isParentActive}
          tooltip={t(title)}
          className={navButtonClassName(isParentActive)}
        >
          <Link
            to={items[0].path}
            className="flex w-full items-center"
            onClick={onItemClick}
            aria-label={t(title)}
            title={t(title)}
          >
            {renderIcon()}
            <span className={labelClassName}>{t(title)}</span>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  return (
    <Collapsible className="group/collapsible" open={isOpen} onOpenChange={onToggle}>
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            isActive={isParentActive}
            tooltip={t(title)}
            className={navButtonClassName(isParentActive)}
          >
            {renderIcon()}
            <span className={labelClassName}>{t(title)}</span>
            <ChevronRight
              strokeWidth={strokeWidth}
              className={cn(
                'ml-auto h-4 w-4 shrink-0 transition-transform group-data-[state=open]/collapsible:rotate-90',
                'group-data-[collapsible=icon]:hidden',
                isParentActive ? 'text-primary-600' : 'text-medium-emphasis'
              )}
            />
          </SidebarMenuButton>
        </CollapsibleTrigger>

        <CollapsibleContent>
          {/* mx-0/border-0 strip the default sub-list margin and connector border;
              pl-4 (1rem) indents the children under the parent. */}
          <SidebarMenuSub className="mx-0 border-0 pl-4 pr-0">
            {items.map((child) => {
              const isChildActive = child.id === activeChildId;
              return (
                <SidebarMenuSubItem key={child.id}>
                  <SidebarMenuSubButton
                    asChild
                    isActive={isChildActive}
                    className={cn(
                      'relative h-9 translate-x-0 overflow-hidden rounded-md px-3 font-medium transition-colors duration-200',
                      isChildActive
                        ? 'bg-primary-50 text-primary-600 hover:bg-primary-50 hover:text-primary-600 data-[active=true]:bg-primary-50 data-[active=true]:text-primary-600'
                        : 'hover:bg-black/[0.04] dark:hover:bg-white/[0.06]'
                    )}
                  >
                    <Link
                      to={child.path}
                      className="flex w-full items-center"
                      onClick={onItemClick}
                      aria-current={isChildActive ? 'page' : undefined}
                    >
                      <div className="flex size-[18px] shrink-0 items-center justify-center">
                        {child.icon && (
                          <MenuIcon
                            name={child.icon as MenuIconName}
                            size={SIDEBAR_NAV_ICON_SIZE}
                            strokeWidth={strokeWidth}
                            weight={isChildActive ? 'fill' : 'regular'}
                            className={isChildActive ? 'text-primary-600' : 'text-neutral-700'}
                          />
                        )}
                      </div>
                      <span
                        className={cn(
                          'truncate text-sm',
                          !showText && 'hidden',
                          isChildActive ? 'text-primary-600' : 'text-high-emphasis'
                        )}
                      >
                        {t(child.name)}
                      </span>
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              );
            })}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
};
